import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState({ status: "polling", message: "Confirming your payment…" });
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/");
      return;
    }

    let cancelled = false;
    const MAX = 10;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await checkAuth();
          setState({ status: "paid", message: "You're enrolled. Welcome aboard." });
          setTimeout(() => navigate("/dashboard", { replace: true }), 1800);
          return;
        }
        if (data.status === "expired") {
          setState({ status: "failed", message: "Checkout session expired. Please try again." });
          return;
        }
        if (attempts.current >= MAX) {
          setState({ status: "failed", message: "Payment not confirmed in time. Please check your email or contact support." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "failed", message: "Could not verify payment status." });
      }
    };

    poll();

    return () => { cancelled = true; };
  }, [params, navigate, checkAuth]);

  const Icon = state.status === "paid" ? CheckCircle2 : state.status === "failed" ? AlertCircle : Loader2;
  const color = state.status === "paid" ? "text-[#1A3626]" : state.status === "failed" ? "text-[#9E3C3C]" : "text-[#C56B52]";

  return (
    <div className="min-h-screen bg-[#F7F6F0] flex items-center justify-center px-6" data-testid="checkout-success">
      <div className="card-flat p-12 max-w-lg w-full text-center">
        <Icon size={48} className={`${color} mx-auto mb-6 ${state.status === "polling" ? "animate-spin" : ""}`} strokeWidth={1.5} />
        <div className="overline mb-3">
          {state.status === "paid" ? "Payment confirmed" : state.status === "failed" ? "Something went wrong" : "Processing"}
        </div>
        <h1 className="font-serif-display text-3xl text-[#121614] leading-tight mb-6">{state.message}</h1>
        {state.status === "failed" && (
          <button onClick={() => navigate("/")} className="btn-primary" data-testid="checkout-back-home">
            Back to home
          </button>
        )}
      </div>
    </div>
  );
}
